// API Client for fetching data from Xano backend
// Uses GET with query parameters for all endpoints
// Includes robust response normalization and label mapping

import { API_BASE_URL } from './apiConfig';

// --- Type Definitions ---

export interface BoatBrand {
    id: number;
    name: string;
    popular?: boolean;
    label: string;
    value: number;
}

export interface EngineBrand {
    id: number;
    name: string;
    label: string;
    value: number;
}

export interface BoatModel {
    id: number;
    name: string;
    boat_brand_id?: number;
    label: string;
    value: number;
}

export interface EngineModel {
    id: number;
    name: string;
    engine_brand_id?: number;
    label: string;
    value: number;
}

export interface ServicePackage {
    id: number;
    title: string;
    description: string;
    price: string;
    features: string[];
    engine_brand_id: number;
    engine_model_id: number;
    label: string;
    value: number;
}

// --- Response Normalization ---

/**
 * Normalize API response to always return an array
 * Handles: array, { items: [] }, { data: [] }, or single object
 */
function normalizeResponse<T>(json: any): T[] {
    if (Array.isArray(json)) {
        return json;
    }
    if (json && typeof json === 'object') {
        if (Array.isArray(json.items)) return json.items;
        if (Array.isArray(json.data)) return json.data;
        if (Array.isArray(json.results)) return json.results;
        // Single object? Wrap in array
        if (json.id !== undefined) return [json];
    }
    console.warn('[API] Unexpected response shape:', json);
    return [];
}

/**
 * Extract a label from an item using common field names
 */
function extractLabel(item: any): string {
    const label =
        item.name ??
        item.title ??
        item.model ??
        item.model_name ??
        item.display_name ??
        item.hull_model ??
        item.engine_model ??
        item.brand_name ??
        item.slug ??
        null;

    if (label !== null && label !== undefined && String(label).trim() !== '') {
        return String(label);
    }

    // Fallback to ID if no label found
    if (item.id !== undefined) {
        return `Item ${item.id}`;
    }

    return 'Unknown';
}

/**
 * Extract a numeric ID from an item
 */
function extractId(item: any): number {
    if (typeof item.id === 'number') return item.id;
    if (typeof item.id === 'string') return parseInt(item.id, 10) || 0;
    return 0;
}

// --- Response Mapping ---

function mapBrandResponse(items: any[]): BoatBrand[] | EngineBrand[] {
    return items.map(item => ({
        ...item,
        id: extractId(item),
        name: extractLabel(item),
        label: extractLabel(item),
        value: extractId(item),
    }));
}

function mapModelResponse(items: any[]): BoatModel[] | EngineModel[] {
    return items.map(item => {
        const id = extractId(item);
        const label = extractLabel(item);
        return {
            ...item,
            id,
            name: label,
            label,
            value: id,
        };
    });
}

function mapPackageResponse(items: any[]): ServicePackage[] {
    console.log('[XANO] service-packages raw items to map:', items);

    return items.map(item => {
        // ID from package_id field
        const id = item.package_id ?? extractId(item);

        // Title from name field
        const title =
            item.name ??
            item.title ??
            item.package_name ??
            'Huoltopaketti';

        // Description (may contain line breaks and bullets)
        const descriptionRaw =
            item.description ??
            item.short_description ??
            '';

        // Price from total_price_decimal
        let priceString = '0 €';
        const priceValue = item.total_price_decimal ?? item.price ?? item.price_eur ?? 0;
        if (typeof priceValue === 'number') {
            priceString = `${priceValue} €`;
        } else if (typeof priceValue === 'string') {
            priceString = priceValue.includes('€') ? priceValue : `${priceValue} €`;
        }

        // Convert description to bullets array
        // Split by line breaks, trim, remove leading ✓/•/- symbols
        let features: string[] = [];
        if (descriptionRaw && typeof descriptionRaw === 'string') {
            features = descriptionRaw
                .split(/\r?\n/)
                .map(line => line.trim())
                .filter(line => line.length > 0)
                .map(line => {
                    // Remove leading bullet symbols: ✓ • - * 
                    return line.replace(/^[✓•\-*]\s*/, '').trim();
                })
                .filter(line => line.length > 0);
        }

        // If no features from description, try other fields
        if (features.length === 0) {
            const featuresRaw = item.items ?? item.features ?? item.bullets ?? [];
            if (Array.isArray(featuresRaw)) {
                features = featuresRaw
                    .filter(f => f !== null && f !== undefined)
                    .map(f => String(f));
            }
        }

        const mapped: ServicePackage = {
            ...item,
            id,
            title,
            description: descriptionRaw,
            price: priceString,
            features,
            engine_brand_id: item.engine_brand_id ?? 0,
            engine_model_id: item.engine_model_id ?? 0,
            label: title,
            value: id,
        };

        console.log('[XANO] Mapped package:', { id, title, price: priceString, featuresCount: features.length, features });
        return mapped;
    });
}

// --- Cache Implementation ---

interface CacheEntry<T> {
    data: T;
    timestamp: number;
    ttl: number;
}

class DataCache {
    private cache: Map<string, CacheEntry<any>> = new Map();

    set<T>(key: string, data: T, ttl: number): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl,
        });
    }

    get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        if (!entry) return null;

        const isExpired = Date.now() - entry.timestamp > entry.ttl;
        if (isExpired) {
            this.cache.delete(key);
            return null;
        }

        return entry.data as T;
    }

    clear(key?: string): void {
        if (key) {
            this.cache.delete(key);
        } else {
            this.cache.clear();
        }
    }
}

const cache = new DataCache();

// Cache TTL: 24 hours
const CACHE_TTL_24H = 24 * 60 * 60 * 1000;

// --- HTTP Helper Functions ---

/**
 * GET request with optional query parameters
 */
async function apiGet<T>(
    path: string,
    params?: Record<string, string | number | undefined>
): Promise<T> {
    let fullUrl = `${API_BASE_URL}${path}`;

    if (params) {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null) {
                searchParams.append(key, String(value));
            }
        }
        const queryString = searchParams.toString();
        if (queryString) {
            fullUrl = `${fullUrl}?${queryString}`;
        }
    }

    console.debug('[API URL]', fullUrl);

    const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('[API ERROR]', fullUrl, response.status, errorText);
        throw new Error(`GET ${path} failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
}

// --- Public Fetch Functions ---

export async function fetchBoatBrands(): Promise<BoatBrand[]> {
    const cacheKey = 'boat-brands';
    const cached = cache.get<BoatBrand[]>(cacheKey);
    if (cached !== null) {
        console.debug('[API CACHE] Using cached boat-brands');
        return cached;
    }

    const rawJson = await apiGet<any>('/boat-brands');
    console.log('[XANO] boat-brands response:', rawJson);

    const normalized = normalizeResponse<any>(rawJson);
    const data = mapBrandResponse(normalized) as BoatBrand[];

    console.debug('[API] Mapped boat brands:', data.length, 'items');
    cache.set(cacheKey, data, CACHE_TTL_24H);
    return data;
}

export async function fetchEngineBrands(): Promise<EngineBrand[]> {
    const cacheKey = 'engine-brands';
    const cached = cache.get<EngineBrand[]>(cacheKey);
    if (cached !== null) {
        console.debug('[API CACHE] Using cached engine-brands');
        return cached;
    }

    const rawJson = await apiGet<any>('/engine-brands');
    console.log('[XANO] engine-brands response:', rawJson);

    const normalized = normalizeResponse<any>(rawJson);
    const data = mapBrandResponse(normalized) as EngineBrand[];

    console.debug('[API] Mapped engine brands:', data.length, 'items');
    cache.set(cacheKey, data, CACHE_TTL_24H);
    return data;
}

/**
 * Fetch boat models for a specific brand
 * GET /boat-models?boat_brand_id=<id>
 */
export async function fetchBoatModels(boatBrandId: number): Promise<BoatModel[]> {
    if (!Number.isFinite(boatBrandId) || boatBrandId <= 0) {
        console.error('[API ERROR] fetchBoatModels: Invalid boatBrandId:', boatBrandId);
        throw new Error('Invalid boatBrandId: must be a positive number');
    }

    const cacheKey = `boat-models-${boatBrandId}`;
    const cached = cache.get<BoatModel[]>(cacheKey);
    if (cached !== null) {
        console.debug('[API CACHE] Using cached boat-models for brand', boatBrandId);
        return cached;
    }

    const rawJson = await apiGet<any>('/boat-models', { boat_brand_id: boatBrandId });

    // DEV: Log raw response to see actual shape
    console.log('[XANO] boat-models response for brand', boatBrandId, ':', rawJson);

    const normalized = normalizeResponse<any>(rawJson);
    const data = mapModelResponse(normalized) as BoatModel[];

    console.debug('[API] Mapped boat models:', data.length, 'items. Sample:', data[0]);
    cache.set(cacheKey, data, CACHE_TTL_24H);
    return data;
}

/**
 * Fetch engine models for a specific brand
 * GET /engine-models?engine_brand_id=<id>
 */
export async function fetchEngineModels(engineBrandId: number): Promise<EngineModel[]> {
    if (!Number.isFinite(engineBrandId) || engineBrandId <= 0) {
        console.error('[API ERROR] fetchEngineModels: Invalid engineBrandId:', engineBrandId);
        throw new Error('Invalid engineBrandId: must be a positive number');
    }

    const cacheKey = `engine-models-${engineBrandId}`;
    const cached = cache.get<EngineModel[]>(cacheKey);
    if (cached !== null) {
        console.debug('[API CACHE] Using cached engine-models for brand', engineBrandId);
        return cached;
    }

    const rawJson = await apiGet<any>('/engine-models', { engine_brand_id: engineBrandId });

    // DEV: Log raw response to see actual shape
    console.log('[XANO] engine-models response for brand', engineBrandId, ':', rawJson);

    const normalized = normalizeResponse<any>(rawJson);
    const data = mapModelResponse(normalized) as EngineModel[];

    console.debug('[API] Mapped engine models:', data.length, 'items. Sample:', data[0]);
    cache.set(cacheKey, data, CACHE_TTL_24H);
    return data;
}

/**
 * Fetch service packages for a specific engine configuration
 * GET /service-packages?engine_brand_id=<id>&engine_model_id=<id>&engine_year=<year>
 */
export async function fetchServicePackages(
    engineBrandId: number,
    engineModelId: number,
    engineYear?: number
): Promise<ServicePackage[]> {
    if (!Number.isFinite(engineBrandId) || engineBrandId <= 0) {
        console.error('[API ERROR] fetchServicePackages: Invalid engineBrandId:', engineBrandId);
        throw new Error('Invalid engineBrandId: must be a positive number');
    }
    if (!Number.isFinite(engineModelId) || engineModelId <= 0) {
        console.error('[API ERROR] fetchServicePackages: Invalid engineModelId:', engineModelId);
        throw new Error('Invalid engineModelId: must be a positive number');
    }

    const params: Record<string, number | undefined> = {
        engine_brand_id: engineBrandId,
        engine_model_id: engineModelId,
    };

    if (engineYear !== undefined && Number.isFinite(engineYear) && engineYear > 0) {
        params.engine_year = engineYear;
    }

    const rawJson = await apiGet<any>('/service-packages', params);
    console.log('[XANO] service-packages raw response:', rawJson);

    // Xano returns: { packages: [...], match_count, has_results }
    // Extract the packages array
    const packagesArray = rawJson.packages || rawJson.data || (Array.isArray(rawJson) ? rawJson : []);
    console.log('[XANO] service-packages array extracted:', packagesArray);

    if (!Array.isArray(packagesArray)) {
        console.warn('[API] Expected packages array but got:', typeof packagesArray);
        return [];
    }

    return mapPackageResponse(packagesArray);
}

// Export cache for manual clearing if needed
export { cache };
